import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { RAG_COLORS, formatCurrency, formatRatio } from 'utils/rag';
import type { AggregateKPI } from 'utils/aggregateMetrics';

interface SelectionChip {
  id: string; // 'branch:78' or 'broker:john@acme.com'
  label: string;
}

interface Props {
  kpi: AggregateKPI;
  chips: SelectionChip[];
  onRemove: (id: string) => void;
}

function AggStat({
  label,
  value,
  ragBg,
  ragText,
}: {
  label: string;
  value: string;
  ragBg?: string;
  ragText?: string;
}) {
  return (
    <Box
      bg="white"
      borderRadius="12px"
      p="16px"
      boxShadow="0px 2px 8px rgba(0,0,0,0.06)"
      borderLeft="4px solid"
      borderColor={ragText ?? '#E2E8F0'}
    >
      <Text
        fontSize="xs"
        fontWeight="600"
        color="gray.500"
        textTransform="uppercase"
        mb="4px"
      >
        {label}
      </Text>
      {ragBg && ragText ? (
        <Box
          display="inline-block"
          px="10px"
          py="2px"
          borderRadius="6px"
          bg={ragBg}
          color={ragText}
          fontWeight="700"
          fontSize="xl"
        >
          {value}
        </Box>
      ) : (
        <Text fontSize="xl" fontWeight="700" color="gray.800">
          {value}
        </Text>
      )}
    </Box>
  );
}

export const AggregateKPIBar = ({ kpi, chips, onRemove }: Props) => {
  const scoreColors = RAG_COLORS[kpi.rag_score];
  const closingColors = RAG_COLORS[kpi.rag_closing];
  const quotesColors = RAG_COLORS[kpi.rag_quotes];

  return (
    <Box mb="20px">
      {/* Selection chips header */}
      <Flex
        align="center"
        justify="space-between"
        mb="12px"
        flexWrap="wrap"
        gap="8px"
      >
        <Flex align="center" gap="8px" flexWrap="wrap">
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            textTransform="uppercase"
            whiteSpace="nowrap"
          >
            {kpi.count} {kpi.count === 1 ? 'entity' : 'entities'} aggregated
          </Text>
          <Wrap spacing="6px">
            {chips.map((chip) => (
              <WrapItem key={chip.id}>
                <Tag
                  size="sm"
                  borderRadius="6px"
                  colorScheme={
                    chip.id.startsWith('branch:') ? 'brand' : 'green'
                  }
                  variant="solid"
                >
                  <TagLabel fontSize="xs">{chip.label}</TagLabel>
                  <TagCloseButton onClick={() => onRemove(chip.id)} />
                </Tag>
              </WrapItem>
            ))}
            {chips.length === 0 && (
              <Text fontSize="xs" color="gray.400" fontStyle="italic">
                Check rows in the table below to add entities
              </Text>
            )}
          </Wrap>
        </Flex>
      </Flex>

      {/* Aggregate KPI cards */}
      {kpi.count > 0 ? (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} gap="12px">
          <AggStat
            label="Score"
            value={String(kpi.score)}
            ragBg={scoreColors.bg}
            ragText={scoreColors.text}
          />
          <AggStat
            label="NB Premium"
            value={formatCurrency(kpi.nb_premium_total)}
          />
          <AggStat
            label="Quotes"
            value={String(kpi.quotes)}
            ragBg={quotesColors.bg}
            ragText={quotesColors.text}
          />
          <AggStat
            label="Closing %"
            value={formatRatio(kpi.closing_ratio)}
            ragBg={closingColors.bg}
            ragText={closingColors.text}
          />
          <AggStat
            label="Avg Premium"
            value={formatCurrency(kpi.avg_premium_total)}
          />
        </SimpleGrid>
      ) : (
        <Box
          bg="gray.50"
          borderRadius="12px"
          border="1px dashed"
          borderColor="gray.200"
          py="32px"
          textAlign="center"
        >
          <Text fontSize="sm" color="gray.400">
            Check branches or brokers in the table below to aggregate their
            combined performance.
          </Text>
        </Box>
      )}
    </Box>
  );
};
